/**
 * Schemas para adelantos tab (progresiones individuales por niña).
 *
 * Per FWA-WORKER-028 G0 + data-model V1.1 §3.5.
 *
 * PII tier: Alto (referencia nina_entity_id; texto comentario/motivo_revocacion
 * pueden contener info contextual de menor — audit summary debe omitirlos).
 *
 * State machine:
 *   borrador → otorgado    (OK, fecha_otorgamiento requerida)
 *   borrador → revocado    (BLOQUEADO)
 *   otorgado → revocado    (OK, fecha_revocacion + motivo_revocacion requeridos)
 *   otorgado → borrador    (BLOQUEADO — irreversible)
 *   revocado → *           (BLOQUEADO — terminal)
 */
import { z } from 'zod';
import { UlidSchema, VersionedFieldsSchema } from './common.js';

// ─── Enums ─────────────────────────────────────────────────────────────────

export const EstatusAdelantoSchema = z.enum(['borrador', 'otorgado', 'revocado']);
export type EstatusAdelanto = z.infer<typeof EstatusAdelantoSchema>;

export const TipoAdelantoSchema = z.enum([
  'investidura',
  'especialidad',
  'insignia',
  'cargo',
  'etapa',
]);
export type TipoAdelanto = z.infer<typeof TipoAdelantoSchema>;

// ─── Master schema `adelantos` (V1.1 §3.5) ─────────────────────────────────

export const AdelantoSchema = VersionedFieldsSchema.extend({
  nina_entity_id: UlidSchema,
  tipo_adelanto: TipoAdelantoSchema,
  nombre_adelanto: z.string().min(2).max(120),
  etapa_anterior: z.string().nullable(),
  etapa_nueva: z.string().nullable(),
  fecha_otorgamiento: z.string().date().nullable(),
  fecha_revocacion: z.string().date().nullable(),
  comentario: z.string().max(500).nullable(),
  motivo_revocacion: z.string().max(500).nullable(),
  estatus: EstatusAdelantoSchema,
});
export type Adelanto = z.infer<typeof AdelantoSchema>;

// ─── Create payload — estatus inicial siempre 'borrador' ───────────────────

export const AdelantoCreatePayloadSchema = z.object({
  nina_entity_id: UlidSchema,
  tipo_adelanto: TipoAdelantoSchema,
  nombre_adelanto: AdelantoSchema.shape.nombre_adelanto,
  etapa_anterior: AdelantoSchema.shape.etapa_anterior.optional(),
  etapa_nueva: AdelantoSchema.shape.etapa_nueva.optional(),
  comentario: AdelantoSchema.shape.comentario.optional(),
});
export type AdelantoCreatePayload = z.infer<typeof AdelantoCreatePayloadSchema>;

// ─── Update payload — partial, no nina_entity_id, no created_*, no entity_id

export const AdelantoUpdatePayloadSchema = z.object({
  tipo_adelanto: TipoAdelantoSchema.optional(),
  nombre_adelanto: AdelantoSchema.shape.nombre_adelanto.optional(),
  etapa_anterior: AdelantoSchema.shape.etapa_anterior.optional(),
  etapa_nueva: AdelantoSchema.shape.etapa_nueva.optional(),
  comentario: AdelantoSchema.shape.comentario.optional(),
  fecha_otorgamiento: AdelantoSchema.shape.fecha_otorgamiento.optional(),
  fecha_revocacion: AdelantoSchema.shape.fecha_revocacion.optional(),
  motivo_revocacion: AdelantoSchema.shape.motivo_revocacion.optional(),
  estatus: EstatusAdelantoSchema.optional(),
});
export type AdelantoUpdatePayload = z.infer<typeof AdelantoUpdatePayloadSchema>;

// ─── State machine validation ──────────────────────────────────────────────

/**
 * Transitions permitidas (current → target).
 * Cualquier combinación no listada se considera BLOQUEADA.
 */
const ALLOWED_TRANSITIONS: Record<EstatusAdelanto, EstatusAdelanto[]> = {
  borrador: ['otorgado'],
  otorgado: ['revocado'],
  revocado: [],
};

export interface StateTransitionInput {
  current: EstatusAdelanto;
  target: EstatusAdelanto;
  fecha_otorgamiento?: string | null | undefined;
  fecha_revocacion?: string | null | undefined;
  motivo_revocacion?: string | null | undefined;
}

export interface StateTransitionResult {
  valid: boolean;
  reason: string | null;
}

export function validateStateTransition(input: StateTransitionInput): StateTransitionResult {
  const { current, target } = input;
  if (current === target) {
    return { valid: true, reason: null };
  }
  const allowed = ALLOWED_TRANSITIONS[current] ?? [];
  if (!allowed.includes(target)) {
    return {
      valid: false,
      reason: `Invalid state transition: ${current} → ${target}`,
    };
  }
  if (target === 'otorgado' && !input.fecha_otorgamiento) {
    return {
      valid: false,
      reason: 'Transition → otorgado requires fecha_otorgamiento',
    };
  }
  if (target === 'revocado') {
    if (!input.fecha_revocacion) {
      return {
        valid: false,
        reason: 'Transition → revocado requires fecha_revocacion',
      };
    }
    if (!input.motivo_revocacion || input.motivo_revocacion.trim().length === 0) {
      return {
        valid: false,
        reason: 'Transition → revocado requires motivo_revocacion',
      };
    }
  }
  return { valid: true, reason: null };
}
